import React from "react";
import { NavigationProps } from "./dashboard.types";
import styles from "./Dashboard.module.css";

/**
 * Dashboard Navigation Component
 *
 * Handles the main navigation items in the sidebar. Each item represents
 * a different section of the dashboard (overview, history, plans, settings).
 */
const DashboardNavigation = ({
  activeSection,
  onSectionChange,
}: NavigationProps) => {
  /**
   * Handle navigation item clicks
   * @param sectionName - The section to navigate to
   */
  const handleNavClick = (sectionName: typeof activeSection) => {
    onSectionChange(sectionName);
  };

  return (
    <nav className={styles.navigation}>
      <div
        className={`${styles.navItem} ${activeSection === "overview" ? styles.active : ""
          }`}
        onClick={() => handleNavClick("overview")}
      >
        <span className={styles.navIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20.25 3.75H3.75C3.35218 3.75 2.97064 3.90804 2.68934 4.18934C2.40804 4.47064 2.25 4.85218 2.25 5.25V18.75C2.25 19.1478 2.40804 19.5294 2.68934 19.8107C2.97064 20.092 3.35218 20.25 3.75 20.25H20.25C20.6478 20.25 21.0294 20.092 21.3107 19.8107C21.592 19.5294 21.75 19.1478 21.75 18.75V5.25C21.75 4.85218 21.592 4.47064 21.3107 4.18934C21.0294 3.90804 20.6478 3.75 20.25 3.75ZM20.25 5.25V9H3.75V5.25H20.25ZM3.75 10.5H9V18.75H3.75V10.5ZM20.25 18.75H10.5V10.5H20.25V18.75Z"
              fill="white"
            />
          </svg>
        </span>
        <span className={styles.navText}>Overview</span>
      </div>

      <div
        className={`${styles.navItem} ${activeSection === "referrals" ? styles.active : ""
          }`}
        onClick={() => handleNavClick("referrals")}
      >
        <span className={styles.navIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M13 14.725c0-5.141 3.892-10.519 10-11.725l.984 2.126c-2.215.835-4.163 3.742-4.38 5.746 2.491.392 4.396 2.547 4.396 5.149 0 3.182-2.584 4.979-5.199 4.979-3.015 0-5.801-2.305-5.801-6.275zm-13 0c0-5.141 3.892-10.519 10-11.725l.984 2.126c-2.215.835-4.163 3.742-4.38 5.746 2.491.392 4.396 2.547 4.396 5.149 0 3.182-2.584 4.979-5.199 4.979-3.015 0-5.801-2.305-5.801-6.275z"
              fill="white"
            />
          </svg>
        </span>
        <span className={styles.navText}>Referrals</span>
      </div>

      <div
        className={`${styles.navItem} ${activeSection === "affiliate" ? styles.active : ""
          }`}
        onClick={() => handleNavClick("affiliate")}
      >
        <span className={styles.navIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
              fill="white"
            />
            <path
              d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
              fill="white"
            />
            <path
              d="M21 10.5C21 11.8807 19.8807 13 18.5 13C17.1193 13 16 11.8807 16 10.5C16 9.11929 17.1193 8 18.5 8C19.8807 8 21 9.11929 21 10.5Z"
              fill="white"
            />
            <path
              d="M8 10.5C8 11.8807 6.88071 13 5.5 13C4.11929 13 3 11.8807 3 10.5C3 9.11929 4.11929 8 5.5 8C6.88071 8 8 9.11929 8 10.5Z"
              fill="white"
            />
            <path
              d="M22 19C22 17.3431 20.6569 16 19 16H18C16.8954 16 16 16.8954 16 18V19H22Z"
              fill="white"
            />
            <path
              d="M2 19C2 17.3431 3.34315 16 5 16H6C7.10457 16 8 16.8954 8 18V19H2Z"
              fill="white"
            />
          </svg>
        </span>
        <span className={styles.navText}>Affiliate</span>
      </div>

      <div
        className={`${styles.navItem} ${activeSection === 'settings' ? styles.active : ''}`}
        onClick={() => handleNavClick('settings')}
      >
        <span className={styles.navIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M9.99857 5.41212C9.09145 5.41212 8.20471 5.68111 7.45047 6.18507C6.69623 6.68904 6.10838 7.40534 5.76124 8.24341C5.4141 9.08147 5.32327 10.0037 5.50024 10.8933C5.67721 11.783 6.11403 12.6003 6.75546 13.2417C7.39688 13.8831 8.21411 14.3199 9.1038 14.4969C9.99348 14.6739 10.9157 14.583 11.7537 14.2359C12.5918 13.8888 13.3081 13.3009 13.8121 12.5467C14.316 11.7924 14.585 10.9057 14.585 9.99857C14.5838 8.78255 14.1001 7.61671 13.2403 6.75685C12.3804 5.897 11.2146 5.41338 9.99857 5.41212ZM9.99857 13.0562C9.39382 13.0562 8.80266 12.8769 8.29984 12.5409C7.79701 12.2049 7.40511 11.7274 7.17368 11.1687C6.94226 10.61 6.8817 9.99518 6.99968 9.40205C7.11766 8.80893 7.40888 8.26411 7.83649 7.83649C8.26411 7.40888 8.80893 7.11766 9.40205 6.99968C9.99518 6.8817 10.61 6.94226 11.1687 7.17368C11.7274 7.40511 12.2049 7.79701 12.5409 8.29984C12.8769 8.80266 13.0562 9.39382 13.0562 9.99857C13.0562 10.8095 12.7341 11.5872 12.1606 12.1606C11.5872 12.7341 10.8095 13.0562 9.99857 13.0562ZM18.4071 10.205C18.4109 10.0674 18.4109 9.92977 18.4071 9.79218L19.8327 8.0111C19.9074 7.91759 19.9592 7.80783 19.9837 7.69065C20.0083 7.57348 20.005 7.45218 19.9741 7.33651C19.7404 6.45802 19.3908 5.61454 18.9345 4.8283C18.8748 4.72541 18.7918 4.63788 18.6923 4.57269C18.5928 4.5075 18.4794 4.46644 18.3612 4.45278L16.0947 4.20053C16.0004 4.10116 15.9049 4.0056 15.8081 3.91388L15.5405 1.64167C15.5268 1.52339 15.4856 1.40996 15.4202 1.31042C15.3548 1.21089 15.2671 1.128 15.1641 1.06836C14.3775 0.612917 13.5341 0.263666 12.6558 0.0297242C12.5401 -0.00104622 12.4188 -0.00419339 12.3016 0.0205363C12.1844 0.0452659 12.0747 0.0971805 11.9813 0.172095L10.205 1.59007C10.0674 1.59007 9.92977 1.59007 9.79218 1.59007L8.0111 0.167318C7.91759 0.0925665 7.80783 0.0408252 7.69065 0.0162621C7.57348 -0.0083009 7.45218 -0.00499902 7.33651 0.025902C6.45816 0.260028 5.61474 0.609607 4.8283 1.0655C4.72541 1.12524 4.63788 1.20818 4.57269 1.30771C4.5075 1.40724 4.46644 1.52061 4.45278 1.6388L4.20053 3.9091C4.10116 4.00401 4.0056 4.09956 3.91388 4.19575L1.64167 4.4566C1.52339 4.47037 1.40996 4.51157 1.31042 4.57693C1.21089 4.64229 1.128 4.73001 1.06836 4.83308C0.612917 5.61962 0.263666 6.46303 0.0297242 7.34129C-0.00104622 7.45703 -0.00419339 7.57837 0.0205363 7.69555C0.0452659 7.81273 0.0971805 7.92245 0.172095 8.01588L1.59007 9.79218C1.59007 9.92977 1.59007 10.0674 1.59007 10.205L0.167318 11.986C0.0925665 12.0795 0.0408252 12.1893 0.0162621 12.3065C-0.0083009 12.4237 -0.00499902 12.545 0.025902 12.6606C0.25961 13.5391 0.609212 14.3826 1.0655 15.1688C1.12524 15.2717 1.20818 15.3593 1.30771 15.4244C1.40724 15.4896 1.52061 15.5307 1.6388 15.5444L3.90528 15.7966C4.00019 15.896 4.09574 15.9915 4.19193 16.0833L4.4566 18.3555C4.47037 18.4737 4.51157 18.5872 4.57693 18.6867C4.64229 18.7862 4.73001 18.8691 4.83308 18.9288C5.61962 19.3842 6.46303 19.7335 7.34129 19.9674C7.45703 19.9982 7.57837 20.0013 7.69555 19.9766C7.81273 19.9519 7.92245 19.9 8.01588 19.825L9.79218 18.4071C9.92977 18.4109 10.0674 18.4109 10.205 18.4071L11.986 19.8327C12.0795 19.9074 12.1893 19.9592 12.3065 19.9837C12.4237 20.0083 12.545 20.005 12.6606 19.9741C13.5391 19.7404 14.3826 19.3908 15.1688 18.9345C15.2717 18.8748 15.3593 18.7918 15.4244 18.6923C15.4896 18.5928 15.5307 18.4794 15.5444 18.3612L15.7966 16.0947C15.896 16.0004 15.9915 15.9049 16.0833 15.8081L18.3555 15.5405C18.4737 15.5268 18.5872 15.4856 18.6867 15.4202C18.7862 15.3548 18.8691 15.2671 18.9288 15.1641C19.3842 14.3775 19.7335 13.5341 19.9674 12.6558C19.9982 12.5401 20.0013 12.4188 19.9766 12.3016C19.9519 12.1844 19.9 12.0747 19.825 11.9813L18.4071 10.205ZM16.8687 9.58388C16.8849 9.8601 16.8849 10.137 16.8687 10.4133C16.8573 10.6024 16.9166 10.789 17.0349 10.9369L18.3908 12.631C18.2352 13.1254 18.036 13.6051 17.7955 14.0643L15.6361 14.3089C15.448 14.3298 15.2744 14.4196 15.1488 14.5611C14.9648 14.768 14.7689 14.9639 14.5621 15.1478C14.4206 15.2734 14.3307 15.4471 14.3098 15.6351L14.07 17.7927C13.6109 18.0333 13.1312 18.2325 12.6367 18.388L10.9417 17.0321C10.806 16.9237 10.6375 16.8647 10.4639 16.8649H10.418C10.1418 16.8811 9.86488 16.8811 9.58865 16.8649C9.39954 16.8535 9.21294 16.9127 9.06503 17.0311L7.36613 18.388C6.87169 18.2324 6.39205 18.0332 5.93287 17.7927L5.68826 15.6361C5.66738 15.448 5.5775 15.2744 5.436 15.1488C5.22914 14.9648 5.03325 14.7689 4.84932 14.5621C4.7237 14.4206 4.55007 14.3307 4.36201 14.3098L2.20447 14.069C1.96386 13.6099 1.76465 13.1303 1.60918 12.6358L2.96505 10.9407C3.08345 10.7928 3.1427 10.6062 3.13131 10.4171C3.11507 10.1409 3.11507 9.86392 3.13131 9.5877C3.1427 9.39858 3.08345 9.21198 2.96505 9.06408L1.60918 7.36613C1.76476 6.87169 1.96398 6.39205 2.20447 5.93287L4.36105 5.68826C4.54911 5.66738 4.72275 5.5775 4.84836 5.436C5.03229 5.22914 5.22819 5.03325 5.43505 4.84932C5.57711 4.72362 5.66736 4.54959 5.68826 4.36105L5.92809 2.20447C6.38722 1.96386 6.86687 1.76465 7.36136 1.60918L9.05643 2.96505C9.20434 3.08345 9.39094 3.1427 9.58005 3.13131C9.85628 3.11507 10.1332 3.11507 10.4094 3.13131C10.5985 3.1427 10.7852 3.08345 10.9331 2.96505L12.631 1.60918C13.1254 1.76476 13.6051 1.96398 14.0643 2.20447L14.3089 4.36105C14.3298 4.54911 14.4196 4.72275 14.5611 4.84836C14.768 5.03229 14.9639 5.22819 15.1478 5.43505C15.2734 5.57655 15.4471 5.66643 15.6351 5.6873L17.7927 5.92714C18.0333 6.38626 18.2325 6.86591 18.388 7.3604L17.0321 9.05548C16.9125 9.20463 16.8532 9.39315 16.8658 9.58388H16.8687Z" fill="white"/>
          </svg>
        </span>
        <span className={styles.navText}>Settings</span>
      </div>
    </nav>
  );
};

export default DashboardNavigation;
